import { useCallback, useEffect, useMemo, useState } from "react";

import { useKeyChange } from "@/lib/hooks/use-synced-state";
import type { Tone } from "@/lib/theme/tone";

/**
 * Save feedback bound to the record it belongs to (Action-Feedback-System §A2,
 * 2026-08-03).
 *
 * The reason this is a hook and not four `useState`s at the call site is
 * §1.2 — a correctness bug, not a cosmetic one. `DetailPanelShell.tsx:114`
 * renders `BearbeitungForm` without a `key` and
 * `app/(app)/isms/catalogue/page.tsx:258` renders `DetailPanelShell` without one
 * either, so picking a different requirement never remounts the form: the
 * `saved`, `saveError`, `conflict` and `readOnly` state of requirement A is
 * still on screen for requirement B. Whatever holds action feedback must
 * therefore be **bound to a subject** and clear itself when that subject
 * changes — otherwise the bug survives its own fix.
 *
 * `subject` is consequently mandatory and positional (a requirement id, a
 * tenant slug, a query-owned object — anything referentially stable between
 * real changes; see {@link useKeyChange}). The change is detected **during
 * render**, so no frame ever commits A's "Gespeichert" against B.
 *
 * Two further rules from the spec are encoded here:
 *
 *  - **`message` is mandatory.** The hook must not call the module-global `t()`:
 *    it is not reactive (`lib/i18n.ts:154-165`) and on the server concurrent
 *    requests share its store. Translation stays at the call site.
 *  - **No auto-clear timer as the primary mechanism** (§2.5). Feedback lives
 *    until the subject changes or the form goes dirty again (the call site
 *    calls {@link SaveFeedback.clear} from its `patchDraft`, the pattern of
 *    `EigeneNotizen.tsx:92-95`). In a compliance tool "did that really
 *    persist?" has to stay answerable after looking away. The optional timer
 *    below only *demotes the emphasis*; it never removes the information.
 */

/**
 * How long a success stays emphasised before it demotes to a quiet
 * "Gespeichert um 14:32". Overridable per call via
 * `useSaveFeedback(subject, { demoteAfterMs })` — several suites run under
 * `vi.useFakeTimers()` (one with `shouldAdvanceTime: true`,
 * `components/admin/users/UsersPanel.search.test.tsx:39`), so a constant that
 * tests cannot control is a latent flake.
 */
export const SAVE_FEEDBACK_DEMOTE_MS = 8000;

/** `strong` = fresh result (check mark, full colour); `muted` = the lasting fact. */
export type SaveFeedbackEmphasis = "strong" | "muted";

export interface SaveFeedbackState {
  tone: Tone;
  message: string;
  /** When the result arrived — render it as "Gespeichert um HH:MM" (§2.5.3). */
  at: Date;
  emphasis: SaveFeedbackEmphasis;
}

export interface SaveFeedback {
  state: SaveFeedbackState | null;
  /** Success. Persists until the subject changes or the call site clears it. */
  markSaved(message: string): void;
  /** Failure. Persists — and never demotes — until cleared. */
  markError(message: string): void;
  /** Call this when the form goes dirty again (§2.5.2). */
  clear(): void;
}

export interface SaveFeedbackOptions {
  /**
   * Emphasis-demotion delay in ms. `null` (or `0`) disables the timer entirely,
   * which is the only knob that touches the clock — the message itself never
   * disappears on a timer.
   */
  demoteAfterMs?: number | null;
}

interface Entry {
  tone: Tone;
  message: string;
  at: Date;
}

export function useSaveFeedback(
  subject: unknown,
  { demoteAfterMs = SAVE_FEEDBACK_DEMOTE_MS }: SaveFeedbackOptions = {},
): SaveFeedback {
  const [entry, setEntry] = useState<Entry | null>(null);
  const [demoted, setDemoted] = useState(false);

  // Render-time reset: the subject switched, so the previous record's result is
  // no longer about anything on screen. Doing this in an effect would commit one
  // frame of A's message against B — exactly the bug being fixed.
  const subjectChanged = useKeyChange(subject);
  if (subjectChanged && entry !== null) {
    setEntry(null);
    setDemoted(false);
  }

  const demotable = entry !== null && entry.tone === "success" && !!demoteAfterMs;
  useEffect(() => {
    if (!demotable) return;
    const timer = setTimeout(() => setDemoted(true), demoteAfterMs ?? 0);
    // Unmount (and every new entry) clears it — a leaked timer under a suite's
    // fake clock surfaces as an unhandled rejection somewhere unrelated
    // (`vitest.setup.ts:100-105`).
    return () => clearTimeout(timer);
  }, [demotable, demoteAfterMs, entry]);

  const markSaved = useCallback((message: string) => {
    setEntry({ tone: "success", message, at: new Date() });
    setDemoted(false);
  }, []);

  const markError = useCallback((message: string) => {
    setEntry({ tone: "danger", message, at: new Date() });
    setDemoted(false);
  }, []);

  const clear = useCallback(() => {
    setEntry(null);
    setDemoted(false);
  }, []);

  const state = useMemo<SaveFeedbackState | null>(
    () => (entry ? { ...entry, emphasis: demoted ? "muted" : "strong" } : null),
    [entry, demoted],
  );

  return { state, markSaved, markError, clear };
}
